import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, ChevronDown, ExternalLink, Satellite, TriangleAlert } from "lucide-react";
import { ASSETS, CONTACT_DETAILS, APP_LINKS } from "@/data/products";
import ObfuscatedEmail from "@/components/ObfuscatedEmail";
import AppBadgeLink from "@/components/AppBadgeLink";
import CookieBanner from "@/components/CookieBanner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/plans", label: "Plans" },
  { to: "/about", label: "About" },
  { to: "/solutions", label: "Solutions" },
  { to: "/rv-motorhome", label: "RV / Motorhome" },
  { to: "/products", label: "Hardware" },
  { to: "/resellers", label: "Resellers" },
  { to: "/contact", label: "Contact" },
];

const PORTALS = [
  { label: "Connectivity Portal", href: "https://portal.pttnz.net", icon: Satellite, desc: "SIMs, plans & PTT device management" },
  { label: "PTT NZ Alerts", href: "https://alerts.pttnz.net", icon: TriangleAlert, desc: "Live NZ highway alerts & load pilot ops" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[var(--ptt-paper)]/85 backdrop-blur-md border-b border-[var(--ptt-line)]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo-link">
          <img src={ASSETS.logo} alt="PTT NZ Limited" className="h-10 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="site-nav-desktop">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium tracking-tight transition-colors ${isActive ? "text-[var(--ptt-ink)] signal-underline" : "text-[var(--ptt-muted)] hover:text-[var(--ptt-ink)]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="header-portals-trigger" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--ptt-muted)] hover:text-[var(--ptt-ink)] outline-none focus:text-[var(--ptt-ink)]">
              Portals <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-2 rounded-none border-[var(--ptt-ink)] shadow-[4px_4px_0_var(--ptt-ink)]" data-testid="header-portals-menu">
              <DropdownMenuLabel className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Customer portals</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PORTALS.map((p) => {
                const Icon = p.icon;
                return (
                  <DropdownMenuItem asChild key={p.label} className="rounded-none focus:bg-[var(--ptt-teal)]/15 cursor-pointer">
                    <a href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`portal-link-${p.label.replace(/\s+/g, '-').toLowerCase()}`} className="flex items-start gap-3 px-2.5 py-2.5">
                      <Icon size={16} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-sm font-bold leading-tight">{p.label}</div>
                        <div className="text-[11px] text-[var(--ptt-muted)] mt-0.5 truncate">{p.desc}</div>
                      </div>
                      <ExternalLink size={12} className="text-[var(--ptt-muted)] mt-1 shrink-0" />
                    </a>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 grid place-items-center border border-[var(--ptt-ink)]"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--ptt-line)] bg-[var(--ptt-paper)]" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-2.5 text-base font-medium ${isActive ? "text-[var(--ptt-ink)]" : "text-[var(--ptt-muted)]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-5 pt-4 border-t border-[var(--ptt-line)]">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)] mb-2">Customer portals</div>
              <div className="flex flex-col gap-1.5">
                {PORTALS.map((p) => (
                  <a key={p.label} href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`mobile-portal-${p.label.replace(/\s+/g, '-').toLowerCase()}`} className="flex items-center justify-between py-2 text-sm font-medium text-[var(--ptt-ink)] hover:text-[var(--ptt-teal-deep)]">
                    <span>{p.label}</span>
                    <ExternalLink size={13} className="text-[var(--ptt-muted)]" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[var(--ptt-ink)] text-[var(--ptt-paper)] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-2.5">
            <img src="/assets/logo-footer.png" alt="PTT NZ Limited" className="h-11 w-auto" />
          </div>
          <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-md">
            Locally owned and operated from Christchurch, New Zealand. Delivering mission-critical Push-to-Talk over Cellular, on-body cameras, IoT hardware and multi-network SIMs that connect natively across One NZ, Spark and 2degrees, plus native access to Australia&apos;s key carriers, and 190+ countries worldwide via roaming partners.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow text-white/50">Explore</div>
          <ul className="mt-4 space-y-2 text-sm">
            {NAV.slice(1).map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-white/80 hover:text-[var(--ptt-teal)] transition-colors" data-testid={`footer-link-${n.label.toLowerCase()}`}>{n.label}</Link>
              </li>
            ))}
            <li>
              <Link to="/about#network-architecture" className="text-white/80 hover:text-[var(--ptt-teal)] transition-colors" data-testid="footer-link-network-architecture">Network Architecture</Link>
            </li>
            <li>
              <Link to="/about#faq" className="text-white/80 hover:text-[var(--ptt-teal)] transition-colors" data-testid="footer-link-faq">FAQ</Link>
            </li>
            <li>
              <Link to="/network-outage" className="text-white/80 hover:text-[var(--ptt-teal)] transition-colors" data-testid="footer-link-network-outage">Network Down? Get Help</Link>
            </li>
            <li>
              <Link to="/government-public-safety" className="text-white/80 hover:text-[var(--ptt-teal)] transition-colors" data-testid="footer-link-government">Government &amp; Public Safety</Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow text-white/50">Portals</div>
          <ul className="mt-4 space-y-2 text-sm">
            {PORTALS.map((p) => (
              <li key={p.label}>
                <a href={p.href} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[var(--ptt-teal)] transition-colors inline-flex items-center gap-1.5" data-testid={`footer-portal-${p.label.replace(/\s+/g, '-').toLowerCase()}`}>
                  {p.label} <ExternalLink size={11} />
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-col items-start gap-2.5" data-testid="footer-app-badges">
            <AppBadgeLink
              href={APP_LINKS.appStore}
              badgeSrc={ASSETS.appStoreBadge}
              badgeAlt="Download on the App Store"
              storeName="the App Store"
              testId="footer-app-store-badge"
              className="inline-block transition-opacity hover:opacity-80"
              imgClassName="h-9 w-auto"
            />
            <AppBadgeLink
              href={APP_LINKS.googlePlay}
              badgeSrc={ASSETS.googlePlayBadge}
              badgeAlt="Get it on Google Play"
              storeName="Google Play"
              testId="footer-google-play-badge"
              className="inline-block transition-opacity hover:opacity-80"
              imgClassName="h-9 w-auto"
            />
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow text-white/50">Contact</div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <Phone size={16} className="mt-0.5 text-[var(--ptt-teal)]" />
              <a href={CONTACT_DETAILS.phoneHref} className="text-white/85 hover:text-[var(--ptt-teal)]" data-testid="footer-phone">{CONTACT_DETAILS.phone}</a>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail size={16} className="mt-0.5 text-[var(--ptt-teal)]" />
              <ObfuscatedEmail
                user={CONTACT_DETAILS.emailUser}
                domain={CONTACT_DETAILS.emailDomain}
                className="text-white/85 hover:text-[var(--ptt-teal)] text-sm"
                buttonLabel="Click to reveal email"
                testId="footer-email"
              />
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="mt-0.5 text-[var(--ptt-teal)]" />
              <a href={CONTACT_DETAILS.mapsHref} target="_blank" rel="noreferrer" className="text-white/85 hover:text-[var(--ptt-teal)]" data-testid="footer-address">{CONTACT_DETAILS.address}</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs font-mono-tech text-white/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-5">
            <span>© {new Date().getFullYear()} PTT NZ LIMITED · ALL RIGHTS RESERVED</span>
            <span className="flex items-center gap-x-3 gap-y-1.5 flex-wrap">
              <Link to="/privacy" className="text-white/80 hover:text-[var(--ptt-teal)] signal-underline transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
              <span className="text-white/25" aria-hidden="true">·</span>
              <Link to="/security" className="text-white/80 hover:text-[var(--ptt-teal)] signal-underline transition-colors" data-testid="footer-link-security">Security &amp; Compliance</Link>
              <span className="text-white/25" aria-hidden="true">·</span>
              <Link to="/terms" className="text-white/80 hover:text-[var(--ptt-teal)] signal-underline transition-colors" data-testid="footer-link-terms">Terms of Service</Link>
            </span>
          </div>
          <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] signal-dot inline-block"></span> ONE SIM · NZ · AU · 190+ COUNTRIES</div>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return (
    <div className="min-h-screen flex flex-col bg-[var(--ptt-paper)]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
};

export default Layout;
